import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const AIRLINES = ["Qatar Airways", "Emirates", "Turkish Airlines", "Singapore Airlines", "Cathay Pacific"];

const DEALS = [
  { from: "New York", fromCode: "JFK", to: "London",    toCode: "LHR", saved: "60%", pub: "$3,570", tbc: "$2,625", img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=80" },
  { from: "Chicago",  fromCode: "ORD", to: "Rome",      toCode: "FCO", saved: "50%", pub: "$5,060", tbc: "$3,530", img: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&q=80" },
  { from: "San Francisco", fromCode: "SFO", to: "Frankfurt", toCode: "FRA", saved: "40%", pub: "$5,550", tbc: "$3,870", img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=400&q=80" },
  { from: "Los Angeles",   fromCode: "LAX", to: "Tokyo",     toCode: "HND", saved: "40%", pub: "$6,185", tbc: "$4,571", img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=80" },
  { from: "Miami",   fromCode: "MIA", to: "Dubai",  toCode: "DXB", saved: "50%", pub: "$5,041", tbc: "$3,512", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=80" },
  { from: "Boston",  fromCode: "BOS", to: "Dublin", toCode: "DUB", saved: "40%", pub: "$3,560", tbc: "$2,527", img: "https://images.unsplash.com/photo-1549918864-48ac978761a4?w=400&q=80" },
];

const STEPS = [
  { num: "01", title: "Get in touch",     desc: "Call +1 (855) 855-1221 or request a free quote. Your travel advisor is waiting." },
  { num: "02", title: "We take notes",    desc: "Personalised with care and attention. Use your preferences to your advantage." },
  { num: "03", title: "Check your inbox", desc: "Best matching itineraries at discounted fares are instantly sent via email." },
  { num: "04", title: "Click and fly",    desc: "Book securely online, receive the e-ticket and enjoy your trip." },
];

const REASONS = [
  { icon: "✦", title: "Exclusive Deals",      desc: "Privately negotiated airfares from 50+ airlines with discounts up to 60%" },
  { icon: "⚡", title: "Last Minute Bookings", desc: "Book stress-free even a few hours before boarding on any international airline" },
  { icon: "🗺", title: "Complex Itineraries",  desc: "Mixed cabin and multi-airline combinations tailored to your exact needs" },
  { icon: "🏆", title: "10+ Years Experience", desc: "Over 130 travel advisors with a decade of industry expertise" },
  { icon: "🕐", title: "24/7 Live Support",    desc: "Assistance available from anywhere in the world at any stage of your trip" },
  { icon: "👑", title: "Elite Engagement",     desc: "Dedicated advisor keeps you informed with the latest travel updates" },
];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return mobile;
}

function useIntersection(ref, threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return visible;
}

/* ─────────────────────────────────────────────
   SMALL COMPONENTS
───────────────────────────────────────────── */
function AnimSection({ children, className = "", delay = 0 }) {
  const ref = useRef();
  const visible = useIntersection(ref);
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(50px)",
      transition: `opacity 0.85s ease ${delay}s, transform 0.85s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

function ParticleField() {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const isMob = window.innerWidth < 768;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: isMob ? 30 : 80 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.6 + 0.1,
    }));
    let raf;
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,200,100,${p.alpha})`; ctx.fill();
      });
      if (!isMob) {
        particles.forEach((p, i) => {
          for (let j = i + 1; j < particles.length; j++) {
            const d = Math.hypot(p.x - particles[j].x, p.y - particles[j].y);
            if (d < 100) {
              ctx.beginPath();
              ctx.strokeStyle = `rgba(255,180,80,${0.08 * (1 - d / 100)})`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(p.x, p.y); ctx.lineTo(particles[j].x, particles[j].y); ctx.stroke();
            }
          }
        });
      }
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

function TypewriterText({ words }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const word = words[idx];
    const timer = setTimeout(() => {
      if (!deleting && text === word) { setTimeout(() => setDeleting(true), 1800); }
      else if (deleting && text === "") { setDeleting(false); setIdx((idx + 1) % words.length); }
      else { setText(deleting ? text.slice(0, -1) : word.slice(0, text.length + 1)); }
    }, deleting ? 60 : 120);
    return () => clearTimeout(timer);
  }, [text, deleting, idx]);
  return <span>{text}<span style={{ animation: "blink 1s infinite", borderRight: "3px solid #d4a855" }}>&nbsp;</span></span>;
}

function CountUp({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef();
  const visible = useIntersection(ref, 0.4);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const end = parseInt(target.replace(/\D/g, ""));
    const step = end / (2000 / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ─────────────────────────────────────────────
   MAIN APP
───────────────────────────────────────────── */
export default function App() {
  const isMobile = useIsMobile();
  const [tripType, setTripType]       = useState("oneway");
  const [navScrolled, setNavScrolled] = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [activeCard, setActiveCard]   = useState(null);
  const heroRef = useRef();

  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 60);
      if (heroRef.current && !isMobile) {
        heroRef.current.style.backgroundPositionY = `${50 + window.scrollY * 0.18}%`;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  const searchFields = [
    { icon: "✈", label: "FROM",       placeholder: "Flying from?" },
    { icon: "✈", label: "TO",         placeholder: "Where to?" },
    { icon: "📅", label: "DEPARTURE", placeholder: "Select date" },
    { icon: "📅", label: "RETURN",    placeholder: "+ Add return" },
    { icon: "👤", label: "PASSENGERS",placeholder: "1 Passenger" },
  ];

  /* ─── shared spacing ─── */
  const px   = isMobile ? "16px" : "48px";
  const secV = isMobile ? "60px" : "100px";

  return (
    <div style={{ fontFamily: "'Cormorant Garamond','Georgia',serif", background: "#080810", color: "#fff", overflowX: "hidden" }}>

      {/* ══════ GLOBAL CSS ══════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #d4a855; border-radius: 2px; }

        @keyframes blink       { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes float       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes pulse-ring  { 0%{transform:scale(0.9);opacity:1} 100%{transform:scale(1.6);opacity:0} }
        @keyframes fadeUp      { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin-slow   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes marquee     { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

        .gold-btn  { background:linear-gradient(135deg,#d4a855,#f0c87a,#b8891e); background-size:200% 200%; border:none; cursor:pointer; color:#080810; font-weight:600; font-family:'Outfit',sans-serif; transition:all 0.4s ease; }
        .gold-btn:hover  { background-position:right center; transform:translateY(-2px); box-shadow:0 8px 30px rgba(212,168,85,0.5); }
        .ghost-btn { background:transparent; border:1px solid rgba(212,168,85,0.4); color:#d4a855; cursor:pointer; font-family:'Outfit',sans-serif; transition:all 0.3s ease; }
        .ghost-btn:hover { background:rgba(212,168,85,0.08); border-color:#d4a855; }

        .glass      { background:rgba(255,255,255,0.04); backdrop-filter:blur(20px); border:1px solid rgba(255,255,255,0.08); }
        .glass-gold { background:rgba(212,168,85,0.06);  backdrop-filter:blur(20px); border:1px solid rgba(212,168,85,0.18); }

        .nav-link { text-decoration:none; color:rgba(255,255,255,0.75); font-family:'Outfit',sans-serif; font-size:14px; letter-spacing:0.04em; transition:color 0.3s; position:relative; }
        .nav-link::after { content:''; position:absolute; bottom:-4px; left:0; width:0; height:1px; background:#d4a855; transition:width 0.3s; }
        .nav-link:hover { color:#d4a855; }
        .nav-link:hover::after { width:100%; }

        .input-field { background:transparent; border:none; outline:none; color:#fff; font-family:'Outfit',sans-serif; font-size:14px; width:100%; min-width:0; }
        .input-field::placeholder { color:rgba(255,255,255,0.36); }

        .deal-card { transition:transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s ease; }
        .deal-card:hover { transform:translateY(-10px) scale(1.015); box-shadow:0 24px 60px rgba(0,0,0,0.5); }

        .reason-card { transition:all 0.4s ease; }
        .reason-card:hover { background:rgba(212,168,85,0.07)!important; transform:translateY(-4px); border-color:rgba(212,168,85,0.4)!important; }

        .step-num { font-family:'Cormorant Garamond',serif; font-size:70px; font-weight:700; color:transparent; -webkit-text-stroke:1px rgba(212,168,85,0.16); line-height:1; }

        /* Hamburger */
        .ham span { display:block; width:22px; height:2px; background:#fff; border-radius:2px; transition:all 0.3s; }
        .ham span:nth-child(2) { margin:5px 0; }
        .ham.open span:nth-child(1) { transform:translateY(7px) rotate(45deg); }
        .ham.open span:nth-child(2) { opacity:0; }
        .ham.open span:nth-child(3) { transform:translateY(-7px) rotate(-45deg); }

        /* Mobile drawer */
        .mobile-drawer { position:fixed; inset:0; z-index:90; background:rgba(8,8,16,0.98); backdrop-filter:blur(20px); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:28px; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.16,1,0.3,1); }
        .mobile-drawer.open { transform:translateX(0); }
        .mobile-drawer a { font-family:'Cormorant Garamond',serif; font-size:34px; font-weight:300; color:rgba(255,255,255,0.8); text-decoration:none; letter-spacing:0.04em; transition:color 0.3s; }
        .mobile-drawer a:hover { color:#d4a855; }
      `}</style>

      {/* ══════ NAV ══════ */}
      <nav style={{
        position:"fixed", top:0, left:0, right:0, zIndex:100,
        padding:`0 ${px}`, height:64,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background: navScrolled || menuOpen ? "rgba(8,8,16,0.96)" : "transparent",
        backdropFilter: navScrolled || menuOpen ? "blur(24px)" : "none",
        borderBottom: navScrolled ? "1px solid rgba(212,168,85,0.12)" : "none",
        transition:"all 0.45s ease",
      }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, letterSpacing:"0.1em", color:"#d4a855", zIndex:101 }}>TBC</div>

        {!isMobile && (
          <div style={{ display:"flex", gap:36 }}>
            {["Home","Blog","About Us","Best Deals","Airlines","Contacts"].map(l => (
              <a key={l} href="#" className="nav-link">{l}</a>
            ))}
          </div>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:14, zIndex:101 }}>
          {!isMobile && (
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.38)", fontFamily:"'Outfit',sans-serif", letterSpacing:"0.12em" }}>CALL US 24/7</div>
              <div style={{ fontSize:15, fontWeight:600, color:"#d4a855", fontFamily:"'Outfit',sans-serif" }}>+1 (855) 855-1221</div>
            </div>
          )}
          <div style={{ width:40, height:40, borderRadius:"50%", background:"linear-gradient(135deg,#d4a855,#f0c87a)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, position:"relative", flexShrink:0 }}>
            📞
            <div style={{ position:"absolute", inset:-3, borderRadius:"50%", border:"2px solid rgba(212,168,85,0.4)", animation:"pulse-ring 2s infinite" }} />
          </div>
          {isMobile && (
            <div className={`ham${menuOpen?" open":""}`} onClick={()=>setMenuOpen(o=>!o)} style={{ cursor:"pointer", padding:4 }}>
              <span /><span /><span />
            </div>
          )}
        </div>
      </nav>

      {/* Mobile drawer */}
      <div className={`mobile-drawer${menuOpen?" open":""}`}>
        {["Home","Blog","About Us","Best Deals","Airlines","Contacts"].map(l=>(
          <a key={l} href="#" onClick={()=>setMenuOpen(false)}>{l}</a>
        ))}
        <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:16, color:"#d4a855", marginTop:8 }}>+1 (855) 855-1221</div>
      </div>

      {/* ══════ HERO ══════ */}
      <div ref={heroRef} style={{
        minHeight:"100svh", position:"relative",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        backgroundImage:"url(https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1800&q=90)",
        backgroundSize:"cover", backgroundPosition:"50% 50%",
        padding: isMobile ? "100px 20px 60px" : "80px 40px 60px",
      }}>
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,rgba(8,8,16,0.3) 0%,rgba(8,8,16,0.52) 55%,rgba(8,8,16,0.94) 100%)" }} />
        <ParticleField />

        {!isMobile && (
          <>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:700, height:700, borderRadius:"50%", border:"1px solid rgba(212,168,85,0.06)", animation:"spin-slow 30s linear infinite", pointerEvents:"none" }}>
              <div style={{ position:"absolute", top:0, left:"50%", width:6, height:6, borderRadius:"50%", background:"#d4a855", transform:"translate(-50%,-50%)", boxShadow:"0 0 10px #d4a855" }} />
            </div>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:480, height:480, borderRadius:"50%", border:"1px solid rgba(212,168,85,0.04)", animation:"spin-slow 20s linear infinite reverse", pointerEvents:"none" }} />
          </>
        )}

        <div style={{ position:"relative", zIndex:2, textAlign:"center", width:"100%", maxWidth:900 }}>
          <div style={{ animation:"fadeUp 0.8s ease 0.2s both", fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:18 }}>✦ THINK COMFORT ✦</div>

          <div style={{ animation:"fadeUp 0.8s ease 0.35s both" }}>
            <h1 style={{ fontSize: isMobile?42:80, fontWeight:300, lineHeight:1.1, marginBottom:20, letterSpacing:"-0.02em" }}>
              Travel in{" "}
              <span style={{ fontStyle:"italic", fontWeight:700, color:"#d4a855", textShadow:"0 0 40px rgba(212,168,85,0.4)" }}>
                <TypewriterText words={["Business Class","First Class","Premium Class"]} />
              </span>
            </h1>
          </div>

          {/* Airlines */}
          <div style={{ animation:"fadeUp 0.8s ease 0.5s both", marginBottom: isMobile?28:44, overflow:"hidden" }}>
            {isMobile ? (
              <div style={{ overflow:"hidden" }}>
                <div style={{ display:"flex", gap:40, animation:"marquee 12s linear infinite", whiteSpace:"nowrap", width:"max-content" }}>
                  {[...AIRLINES,...AIRLINES].map((a,i)=>(
                    <span key={i} style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, letterSpacing:"0.1em", color:"rgba(255,255,255,0.32)", fontWeight:500 }}>{a.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", gap:40, alignItems:"center", justifyContent:"center", flexWrap:"wrap" }}>
                {AIRLINES.map(a=>(
                  <span key={a} style={{ fontFamily:"'Outfit',sans-serif", fontSize:12, letterSpacing:"0.1em", color:"rgba(255,255,255,0.36)", fontWeight:500 }}>{a.toUpperCase()}</span>
                ))}
                <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:12, color:"rgba(255,255,255,0.2)", letterSpacing:"0.1em" }}>AND OTHERS…</span>
              </div>
            )}
          </div>

          {/* Search form */}
          <div style={{ animation:"fadeUp 0.8s ease 0.65s both" }}>
            {/* Trip-type tabs */}
            <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:14, flexWrap:"wrap" }}>
              {[["roundtrip","Round trip"],["oneway","One way"],["multicity","Multi city"]].map(([val,label])=>(
                <button key={val} onClick={()=>setTripType(val)} style={{
                  padding: isMobile?"6px 14px":"6px 20px", borderRadius:20, border:"none", cursor:"pointer",
                  fontFamily:"'Outfit',sans-serif", fontSize:12, fontWeight:500,
                  background: tripType===val ? "linear-gradient(135deg,#d4a855,#f0c87a)" : "rgba(255,255,255,0.08)",
                  color: tripType===val ? "#080810" : "rgba(255,255,255,0.55)",
                  transition:"all 0.3s ease",
                }}>{label}</button>
              ))}
            </div>

            {isMobile ? (
              /* stacked form */
              <div className="glass" style={{ borderRadius:18, overflow:"hidden" }}>
                {searchFields.map((f,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 18px", borderBottom: i<searchFields.length-1?"1px solid rgba(255,255,255,0.07)":"none" }}>
                    <span style={{ fontSize:14, flexShrink:0 }}>{f.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, color:"#d4a855", fontFamily:"'Outfit',sans-serif", letterSpacing:"0.15em", marginBottom:2 }}>{f.label}</div>
                      <input className="input-field" placeholder={f.placeholder} />
                    </div>
                  </div>
                ))}
                <div style={{ padding:"12px 14px" }}>
                  <button className="gold-btn" style={{ width:"100%", padding:"14px", borderRadius:12, fontSize:15, letterSpacing:"0.08em" }}>SEARCH FLIGHTS →</button>
                </div>
              </div>
            ) : (
              /* single-row form */
              <div style={{ display:"flex", alignItems:"center", background:"rgba(255,255,255,0.06)", backdropFilter:"blur(40px)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:60, padding:"6px 6px 6px 20px" }}>
                {searchFields.map((f,i)=>(
                  <div key={i} style={{ flex: i<2?2:1, padding:"10px 16px", borderRight: i<4?"1px solid rgba(255,255,255,0.09)":"none", minWidth:0 }}>
                    <div style={{ fontSize:9, color:"#d4a855", fontFamily:"'Outfit',sans-serif", letterSpacing:"0.15em", marginBottom:2 }}>{f.label}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:12, flexShrink:0 }}>{f.icon}</span>
                      <input className="input-field" placeholder={f.placeholder} />
                    </div>
                  </div>
                ))}
                <button className="gold-btn" style={{ borderRadius:50, width:52, height:52, fontSize:20, flexShrink:0 }}>→</button>
              </div>
            )}
          </div>

          {/* Tagline */}
          <div style={{ animation:"fadeUp 0.8s ease 0.8s both", marginTop:20, fontFamily:"'Outfit',sans-serif", fontSize: isMobile?12:13, color:"rgba(255,255,255,0.44)", fontStyle:"italic" }}>
            "Last month TBC clients saved an average of{" "}
            <span style={{ color:"#d4a855", fontStyle:"normal", fontWeight:600 }}>$2,400</span> per ticket"
          </div>

          {/* Trust badges */}
          <div style={{ animation:"fadeUp 0.8s ease 0.95s both", display:"flex", gap: isMobile?18:36, justifyContent:"center", marginTop:26, flexWrap:"wrap" }}>
            {[
              { icon:"⭐", title:"Google 4.9★",    sub:"Reviews" },
              { icon:"🌟", title:"Trustpilot 4.5", sub:"3,891 Reviews" },
              { icon:"✈",  title:"ARC",             sub:"Accredited Agency" },
              { icon:"🕐", title:"24/7",            sub:"Live Concierge" },
            ].map(b=>(
              <div key={b.title} style={{ textAlign:"center" }}>
                <div style={{ fontSize: isMobile?16:20, marginBottom:2 }}>{b.icon}</div>
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize: isMobile?11:13, fontWeight:600 }}>{b.title}</div>
                <div style={{ fontSize: isMobile?9:11, color:"rgba(255,255,255,0.36)", letterSpacing:"0.05em" }}>{b.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {!isMobile && (
          <div style={{ position:"absolute", bottom:26, left:"50%", transform:"translateX(-50%)", display:"flex", flexDirection:"column", alignItems:"center", gap:8, animation:"float 2s ease-in-out infinite" }}>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, letterSpacing:"0.2em", color:"rgba(255,255,255,0.26)" }}>SCROLL</div>
            <div style={{ width:1, height:34, background:"linear-gradient(to bottom,rgba(212,168,85,0.55),transparent)" }} />
          </div>
        )}
      </div>

      {/* ══════ STATS BAR ══════ */}
      <div style={{ background:"linear-gradient(135deg,#d4a855,#b8891e)", padding: isMobile?"22px 20px":"28px 60px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns: isMobile?"1fr 1fr":"repeat(4,1fr)", gap: isMobile?"20px 12px":32 }}>
          {[
            { num:"130", suffix:"+",       label:"Travel Advisors" },
            { num:"10",  suffix:"+ Years", label:"Of Experience" },
            { num:"97",  suffix:"%",       label:"Client Satisfaction" },
            { num:"60",  suffix:"%",       label:"Max Savings" },
          ].map(s=>(
            <div key={s.label} style={{ textAlign:"center", color:"#080810" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?40:52, fontWeight:700, lineHeight:1 }}>
                <CountUp target={s.num} suffix={s.suffix} />
              </div>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize: isMobile?10:12, letterSpacing:"0.1em", fontWeight:600, marginTop:4 }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ══════ DEALS ══════ */}
      <div style={{ padding:`${secV} ${px}`, maxWidth:1200, margin:"0 auto" }}>
        <AnimSection>
          <div style={{ textAlign:"center", marginBottom: isMobile?40:70 }}>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:14 }}>SMART SAVINGS</div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?36:64, fontWeight:300, lineHeight:1.15 }}>
              Save with <span style={{ fontStyle:"italic", color:"#d4a855" }}>Travel Business Class</span>
            </h2>
            <p style={{ fontFamily:"'Outfit',sans-serif", fontSize: isMobile?13:16, color:"rgba(255,255,255,0.46)", marginTop:12, maxWidth:560, margin:"12px auto 0" }}>
              By uncovering hidden fares and exclusive offers, we make business and first class travel far more accessible.
            </p>
          </div>
        </AnimSection>

        <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr":"repeat(auto-fill,minmax(340px,1fr))", gap: isMobile?14:24 }}>
          {DEALS.map((d,i)=>(
            <AnimSection key={i} delay={isMobile?0:i*0.07}>
              <div className="deal-card glass-gold" style={{ borderRadius:16, overflow:"hidden", cursor:"pointer" }}
                onMouseEnter={()=>setActiveCard(i)} onMouseLeave={()=>setActiveCard(null)}>
                <div style={{ position:"relative", height: isMobile?160:185, overflow:"hidden" }}>
                  <img src={d.img} alt={d.to} style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform 0.8s ease", transform: activeCard===i?"scale(1.1)":"scale(1)" }} />
                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(8,8,16,0.82),transparent)" }} />
                  <div style={{ position:"absolute", top:10, right:10, background:"linear-gradient(135deg,#d4a855,#f0c87a)", color:"#080810", borderRadius:20, padding:"3px 10px", fontFamily:"'Outfit',sans-serif", fontSize:11, fontWeight:700 }}>SAVE {d.saved}</div>
                  <div style={{ position:"absolute", bottom:12, left:12, right:12 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:12, color:"rgba(255,255,255,0.65)" }}>{d.from} ({d.fromCode})</span>
                      <span style={{ color:"#d4a855", fontSize:16 }}>→</span>
                      <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:600 }}>{d.to} ({d.toCode})</span>
                    </div>
                  </div>
                </div>
                <div style={{ padding:"16px 18px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, color:"rgba(255,255,255,0.36)", letterSpacing:"0.1em", marginBottom:3 }}>PUBLISHED</div>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:16, color:"rgba(255,255,255,0.27)", textDecoration:"line-through" }}>{d.pub}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, color:"#d4a855", letterSpacing:"0.1em", marginBottom:3 }}>TBC FARE</div>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:"#d4a855" }}>{d.tbc}</div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimSection>
          ))}
        </div>

        <AnimSection delay={0.2}>
          <div style={{ textAlign:"center", marginTop:44 }}>
            <button className="gold-btn" style={{ padding: isMobile?"13px 32px":"16px 48px", borderRadius:40, fontSize:13, letterSpacing:"0.1em" }}>VIEW ALL DEALS →</button>
          </div>
        </AnimSection>
      </div>

      {/* ══════ PASSION ══════ */}
      <div style={{ background:"linear-gradient(135deg,rgba(212,168,85,0.04),#080810)", padding:`${secV} ${px}`, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-200, right:-200, width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle,rgba(212,168,85,0.07) 0%,transparent 70%)", pointerEvents:"none" }} />
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns: isMobile?"1fr":"1fr 1fr", gap: isMobile?40:80, alignItems:"center" }}>

          <AnimSection>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:14 }}>PASSION FOR EXCELLENCE</div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?36:56, fontWeight:300, lineHeight:1.25, marginBottom:22 }}>
              Your personal<br /><span style={{ fontStyle:"italic", color:"#d4a855" }}>travel advisor</span><br />awaits.
            </h2>
            <p style={{ fontFamily:"'Outfit',sans-serif", fontSize: isMobile?13:16, color:"rgba(255,255,255,0.5)", lineHeight:1.85, marginBottom:30 }}>
              At TravelBusinessClass you get your personal travel advisor, entirely dedicated to understanding and taking care of your travel needs. We strive to build long lasting relationships inspiring trust and delivering the best service.
            </p>
            <button className="gold-btn" style={{ padding: isMobile?"13px 28px":"16px 40px", borderRadius:40, fontSize:13, letterSpacing:"0.1em" }}>CONTACT US →</button>
          </AnimSection>

          <AnimSection delay={isMobile?0:0.2}>
            <div style={{ position:"relative", marginTop: isMobile?0:0 }}>
              <div style={{ borderRadius:20, overflow:"hidden", aspectRatio:"4/3" }}>
                <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=700&q=85" alt="Business class" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(135deg,rgba(212,168,85,0.1),transparent)" }} />
              </div>
              <div className="glass-gold" style={{ position:"absolute", bottom: isMobile?-12:-24, left: isMobile?8:-24, borderRadius:16, padding: isMobile?"14px 18px":"20px 28px", animation:"float 4s ease-in-out infinite" }}>
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, color:"#d4a855", letterSpacing:"0.1em", marginBottom:3 }}>AVERAGE SAVING</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?26:40, fontWeight:700 }}>$2,400</div>
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:"rgba(255,255,255,0.38)" }}>per ticket this month</div>
              </div>
            </div>
          </AnimSection>
        </div>
      </div>

      {/* ══════ WHY US ══════ */}
      <div style={{ padding:`${secV} ${px}`, maxWidth:1200, margin:"0 auto" }}>
        <AnimSection>
          <div style={{ textAlign:"center", marginBottom: isMobile?40:70 }}>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:14 }}>6 GOOD REASONS</div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?36:64, fontWeight:300 }}>
              Why Book <span style={{ fontStyle:"italic", color:"#d4a855" }}>With Us</span>
            </h2>
          </div>
        </AnimSection>

        <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr 1fr":"repeat(auto-fill,minmax(320px,1fr))", gap: isMobile?10:20 }}>
          {REASONS.map((r,i)=>(
            <AnimSection key={i} delay={isMobile?0:i*0.06}>
              <div className="reason-card glass" style={{ borderRadius:16, padding: isMobile?"20px 16px":"32px 28px", border:"1px solid rgba(255,255,255,0.08)", height:"100%" }}>
                <div style={{ fontSize: isMobile?22:26, marginBottom:12 }}>{r.icon}</div>
                <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?19:26, fontWeight:600, marginBottom:8, color:"#d4a855" }}>{r.title}</h3>
                <p style={{ fontFamily:"'Outfit',sans-serif", fontSize: isMobile?12:15, color:"rgba(255,255,255,0.5)", lineHeight:1.75 }}>{r.desc}</p>
              </div>
            </AnimSection>
          ))}
        </div>
      </div>

      {/* ══════ HOW TO BOOK ══════ */}
      <div style={{ padding:`${secV} ${px}`, background:"linear-gradient(to bottom,#080810,rgba(212,168,85,0.04),#080810)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <AnimSection>
            <div style={{ textAlign:"center", marginBottom: isMobile?44:80 }}>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:14 }}>SIMPLE STEPS</div>
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?36:64, fontWeight:300 }}>
                How to <span style={{ fontStyle:"italic", color:"#d4a855" }}>Book</span>
              </h2>
            </div>
          </AnimSection>

          {isMobile ? (
            /* vertical timeline */
            <div style={{ display:"flex", flexDirection:"column" }}>
              {STEPS.map((s,i)=>(
                <AnimSection key={i} delay={i*0.1}>
                  <div style={{ display:"flex", gap:18, paddingBottom: i<STEPS.length-1?28:0 }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
                      <div style={{ width:42, height:42, borderRadius:"50%", background:"linear-gradient(135deg,#d4a855,#b8891e)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Outfit',sans-serif", fontSize:14, fontWeight:700, color:"#080810", flexShrink:0 }}>{i+1}</div>
                      {i<STEPS.length-1 && <div style={{ width:1, flex:1, minHeight:24, background:"linear-gradient(to bottom,rgba(212,168,85,0.4),rgba(212,168,85,0.04))", marginTop:8 }} />}
                    </div>
                    <div style={{ paddingTop:6 }}>
                      <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:600, marginBottom:6, color:"#fff" }}>{s.title}</h3>
                      <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:13, color:"rgba(255,255,255,0.44)", lineHeight:1.75 }}>{s.desc}</p>
                    </div>
                  </div>
                </AnimSection>
              ))}
            </div>
          ) : (
            /* horizontal grid */
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, position:"relative" }}>
              <div style={{ position:"absolute", top:38, left:"12%", right:"12%", height:1, background:"linear-gradient(to right,rgba(212,168,85,0.08),rgba(212,168,85,0.4),rgba(212,168,85,0.08))", zIndex:0 }} />
              {STEPS.map((s,i)=>(
                <AnimSection key={i} delay={i*0.13}>
                  <div style={{ textAlign:"center", position:"relative", zIndex:1, padding:"0 16px" }}>
                    <div className="step-num">{s.num}</div>
                    <div style={{ width:50, height:50, borderRadius:"50%", background:"linear-gradient(135deg,#d4a855,#b8891e)", display:"flex", alignItems:"center", justifyContent:"center", margin:"-13px auto 18px", fontFamily:"'Outfit',sans-serif", fontSize:15, fontWeight:700, color:"#080810", position:"relative", zIndex:2 }}>{i+1}</div>
                    <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:600, marginBottom:10, color:"#fff" }}>{s.title}</h3>
                    <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:13, color:"rgba(255,255,255,0.43)", lineHeight:1.75 }}>{s.desc}</p>
                  </div>
                </AnimSection>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════ CTA BANNER ══════ */}
      <AnimSection>
        <div style={{ margin: isMobile?"0 16px 60px":"0 40px 100px", borderRadius:20, overflow:"hidden", position:"relative" }}>
          <img src="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1400&q=80" alt="Plane" style={{ width:"100%", height: isMobile?300:380, objectFit:"cover", objectPosition:"center 40%" }} />
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(135deg,rgba(8,8,16,0.88),rgba(8,8,16,0.42))", display:"flex", alignItems:"center", padding: isMobile?"28px 22px":"0 80px" }}>
            <div style={{ width:"100%" }}>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, letterSpacing:"0.4em", color:"#d4a855", marginBottom:12 }}>ONE CALL AWAY</div>
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?30:56, fontWeight:300, lineHeight:1.2, marginBottom:24 }}>
                Your travel advisor<br />is <span style={{ fontStyle:"italic", color:"#d4a855" }}>ready to help</span>
              </h2>
              <div style={{ display:"flex", gap:12, flexDirection: isMobile?"column":"row" }}>
                <button className="gold-btn" style={{ padding: isMobile?"13px 20px":"16px 40px", borderRadius:40, fontSize: isMobile?13:14, letterSpacing:"0.08em" }}>
                  📞 CALL: +1 (855) 855-1221
                </button>
                <button className="ghost-btn" style={{ padding: isMobile?"13px 20px":"16px 40px", borderRadius:40, fontSize: isMobile?13:14, letterSpacing:"0.08em" }}>
                  REQUEST A QUOTE
                </button>
              </div>
            </div>
          </div>
        </div>
      </AnimSection>

      {/* ══════ FOOTER ══════ */}
      <footer style={{ borderTop:"1px solid rgba(212,168,85,0.13)", padding: isMobile?"44px 20px 28px":"60px 60px 40px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns: isMobile?"1fr 1fr":"2fr 1fr 1fr 1fr", gap: isMobile?"32px 16px":60, marginBottom: isMobile?36:60 }}>

            {/* Brand — spans full width on mobile */}
            <div style={{ gridColumn: isMobile?"span 2":"auto" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:"#d4a855", marginBottom:14 }}>TBC</div>
              <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:13, color:"rgba(255,255,255,0.36)", lineHeight:1.85, marginBottom:18 }}>
                TravelBusinessClass.com — making premium air travel accessible through exclusive partnerships and personalised service.
              </p>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:14, color:"#d4a855", marginBottom:5 }}>+1 (855) 855-1221</div>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:13, color:"rgba(255,255,255,0.36)" }}>sales@TravelBusinessClass.com</div>
            </div>

            {[
              { title:"Company",  links:["About Us","Blog","Careers","Press"] },
              { title:"Services", links:["Business Class","First Class","Premium Economy","Best Deals"] },
              { title:"Legal",    links:["Privacy Policy","Terms & Conditions","Cookie Policy","Sitemap"] },
            ].map(col=>(
              <div key={col.title}>
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, letterSpacing:"0.18em", color:"#d4a855", marginBottom:16, fontWeight:600 }}>{col.title.toUpperCase()}</div>
                {col.links.map(l=>(
                  <div key={l} style={{ fontFamily:"'Outfit',sans-serif", fontSize:13, color:"rgba(255,255,255,0.36)", marginBottom:10, cursor:"pointer", transition:"color 0.3s" }}
                    onMouseEnter={e=>e.target.style.color="#d4a855"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.36)"}>
                    {l}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:22, display:"flex", flexDirection: isMobile?"column":"row", justifyContent:"space-between", alignItems: isMobile?"flex-start":"center", gap:14 }}>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:"rgba(255,255,255,0.2)" }}>© 2026 TravelBusinessClass.com — All Rights Reserved</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {["VISA","MASTERCARD","AMEX","DISCOVER","IATA","ARC"].map(b=>(
                <span key={b} style={{ fontFamily:"'Outfit',sans-serif", fontSize:9, letterSpacing:"0.1em", color:"rgba(255,255,255,0.2)", padding:"3px 7px", border:"1px solid rgba(255,255,255,0.07)", borderRadius:4 }}>{b}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Floating call button */}
      <div style={{ position:"fixed", bottom: isMobile?20:32, right: isMobile?20:32, zIndex:99, width:52, height:52, borderRadius:"50%", background:"linear-gradient(135deg,#d4a855,#f0c87a)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, cursor:"pointer", boxShadow:"0 8px 30px rgba(212,168,85,0.4)", transition:"transform 0.3s" }}
        onMouseEnter={e=>e.currentTarget.style.transform="scale(1.1)"}
        onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
        📞
        <div style={{ position:"absolute", inset:-5, borderRadius:"50%", border:"2px solid rgba(212,168,85,0.3)", animation:"pulse-ring 2s infinite" }} />
      </div>
    </div>
  );
}